// @vendors
import React, { useContext, useMemo } from 'react'
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Box,
    TableSortLabel
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

// @assets
import { EmployeeTableStyles } from '../../../assets/css/employeeTable-style';
import { GeneralStyles } from '../../../assets/css/general-style';

// @components
import ToolbarTicket from './ToolbarTicket';
import { TicketContext } from '../TicketManagement';
import { TablePaginationActions } from '../../Admin/components/custom/TablePaginationActions';
import TicketRow from './TicketRow';

const TicketTable = () => {
  const styles = useMemo(() => EmployeeTableStyles(), []);
  const generalStyle = useMemo(() => GeneralStyles(), []);
  const {
    rowsState,
    setRowsState,
    handleMapResponse,
    filterTicket,
    sortBy,
    setSortBy
  } = useContext(TicketContext);

  const handlePageChange = (event, nextPage) => {
      const page = nextPage === 0 ? 1 : nextPage === rowsState.currentPage ? nextPage + 1 : nextPage
      setRowsState((prev) => ({
        ...prev,
        currentPage: parseInt(page),
        pageSize:rowsState.pageSize
      }));
      const params = {
        pagination: {page: page, size: parseInt(rowsState.pageSize)},
        filters: filterTicket,
        orderBy: {sort: sortBy.sort, direction: sortBy.direction}
      }
      handleMapResponse(params);
  };
  
  const handlePageSizeChange = (event) => {
      setRowsState((prev) => ({
        ...prev,
        currentPage: 1,
        pageSize: event.target.value
      }));
      const params = {
        pagination: {size:event.target.value},
        filters: filterTicket,
        orderBy: {sort: sortBy.sort, direction: sortBy.direction}
      }
      handleMapResponse(params);
  };

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const onRequestSort = (event, property) => {
    const isAsc = sortBy.sort === property && sortBy.direction === 'asc';
    sortDispatch(property, isAsc ? 'desc' : 'asc');
    const params = {
      filters: filterTicket,
      orderBy: {sort: property, direction: isAsc ? 'desc' : 'asc'}
    }
    handleMapResponse(params);
  };

  const sortDispatch = (property, option) => {
    return setSortBy({
        sort: property,
        direction: option
      })
  }

  const sortTableCell = (label, nameToSearch) => {
    return (
      <TableSortLabel
        active={sortBy.sort === nameToSearch ? true : false}
        direction={sortBy.sort === nameToSearch ? sortBy.direction : 'asc'}
        onClick={createSortHandler(nameToSearch)}
        sx={{ ...styles.tableHeadTitle}}
      >
        {label}
          <Box component="div" sx={visuallyHidden}>
            {sortBy.direction === 'desc' ? 'sorted descending' : 'sorted ascending'}
          </Box>
      </TableSortLabel>
    )
  }

  return (
    <Paper elevation={4}>
        <ToolbarTicket />
        <TableContainer sx={styles.tableContainer}>
          <Table aria-label="collapsible table" stickyHeader>
            <TableHead>
              <TableRow sx={styles.tableRow}>
                <TableCell sx={styles.emptyTableCell} />

                <TableCell
                  sx={{ ...styles.tableHeadTitle, textAlign: 'left', paddingLeft: '10px' }}
                  sortDirection={sortBy.sort === 'order' ? sortBy.direction : false}
                >
                  {sortTableCell('Id', 'order')}
                </TableCell>
                
                <TableCell
                  sx={{ ...styles.tableHeadTitle, textAlign: 'left', paddingLeft: '10px' }}
                  sortDirection={sortBy.sort === 'name' ? sortBy.direction : false}
                >
                  {sortTableCell('Name', 'name')}
                </TableCell>

                <TableCell
                  sx={{ ...styles.tableHeadTitle, ...generalStyle.tableColumnHide, textAlign: 'left' }}
                  sortDirection={sortBy.sort === 'create' ? sortBy.direction : false}  
                >
                  {sortTableCell('Created at', 'create')}
                </TableCell>
                
                <TableCell
                  sx={{ ...styles.tableHeadTitle, ...generalStyle.tableColumnHide, textAlign: 'left' }}
                  sortDirection={sortBy.sort === 'update' ? sortBy.direction : false} 
                >
                  {sortTableCell('Updated at', 'update')}
                </TableCell>
                
                <TableCell
                  sx={{ ...styles.tableHeadTitle, ...generalStyle.tableColumnHide, textAlign: 'left' }}
                  sortDirection={sortBy.sort === 'priority' ? sortBy.direction : false} 
                >
                  {sortTableCell('Priority', 'priority')}
                </TableCell>

                <TableCell
                  sx={{ ...styles.tableHeadTitle, textAlign: 'left' }}
                  sortDirection={sortBy.sort === 'status' ? sortBy.direction : false} 
                >
                  {sortTableCell('Resolved', 'status')}
                </TableCell>
                    
                <TableCell sx={styles.emptyTableCell} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsState?.rows?.length
                ? React.Children.toArray(rowsState?.rows?.map((row) => <TicketRow row={row} />))
                : null
              }
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 20, 50]}
          colSpan={3}
          count={rowsState.rowCount || 0}
          rowsPerPage={parseInt(rowsState.pageSize)}
          page={rowsState.page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={(e) => handlePageSizeChange(e)}
          ActionsComponent={() => TablePaginationActions(rowsState.rowCount, rowsState.currentPage, rowsState.pageSize, handlePageChange)}
          SelectProps={{
            inputProps: {
              "aria-label": "rows per page",
            },
            native: true,
          }}
        />
    </Paper>
  )
}

export default TicketTable